import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Brouillons de formulaires — filet de sécurité contre la perte de saisie
 * quand l'app est rechargée (mise à jour, kill du process WebView par
 * Android en background…).
 *
 * IMPÉRATIF localStorage (défaut de zustand/persist) et PAS sessionStorage :
 * `clearAppCaches()` purge sessionStorage à chaque reload de mise à jour —
 * exactement l'événement auquel un brouillon doit survivre.
 *
 * Les clés sont préfixées par salle + utilisateur (multi-salle, multi-compte
 * sur un même appareil) : voir buildDraftKey.
 */

export interface FormDraft {
  data: unknown;
  savedAt: number;
}

interface FormDraftState {
  drafts: Record<string, FormDraft>;
  saveDraft: (key: string, data: unknown) => void;
  getDraft: (key: string) => FormDraft | null;
  clearDraft: (key: string) => void;
  /** Supprime les brouillons plus vieux que maxAgeMs (défaut 24 h). */
  purgeStale: (maxAgeMs?: number) => void;
}

export const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export function buildDraftKey(
  formId: string,
  gymId: string | null | undefined,
  userId: string | null | undefined
): string {
  return `${formId}:${gymId ?? 'no-gym'}:${userId ?? 'anon'}`;
}

export const useFormDraftStore = create<FormDraftState>()(
  persist(
    (set, get) => ({
      drafts: {},

      saveDraft: (key, data) =>
        set((state) => ({
          drafts: { ...state.drafts, [key]: { data, savedAt: Date.now() } },
        })),

      getDraft: (key) => {
        const draft = get().drafts[key];
        if (!draft) return null;
        // Un brouillon périmé n'est jamais rendu (même si purgeStale n'est
        // pas encore passé dessus).
        if (Date.now() - draft.savedAt > DRAFT_MAX_AGE_MS) return null;
        return draft;
      },

      clearDraft: (key) =>
        set((state) => {
          if (!(key in state.drafts)) return state;
          const next = { ...state.drafts };
          delete next[key];
          return { drafts: next };
        }),

      purgeStale: (maxAgeMs = DRAFT_MAX_AGE_MS) =>
        set((state) => {
          const now = Date.now();
          const entries = Object.entries(state.drafts).filter(
            ([, d]) => d && typeof d.savedAt === 'number' && now - d.savedAt <= maxAgeMs
          );
          if (entries.length === Object.keys(state.drafts).length) return state;
          return { drafts: Object.fromEntries(entries) };
        }),
    }),
    {
      name: 'form-drafts',
      version: 1,
      partialize: (state) => ({ drafts: state.drafts }),
      onRehydrateStorage: () => (state) => {
        // L'état vient du disque : purge des entrées périmées ou malformées.
        state?.purgeStale();
      },
    }
  )
);
